import { Box, Dialog, DialogContent, DialogTitle, Stack, useMediaQuery, useTheme } from "@mui/material";
import { FoodsIconButton, SelectionCardGroup, type SelectionCardOption, Typography } from "@foodpilot/foods";
import { FoodsIcon } from "@foodpilot/foods/Icons";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";

type PackagingCreateChoiceModalProps = {
    appearance?: "legacy" | "product-v2";
    open: boolean;
    onClose: () => void;
    onSelectElement: () => void;
    onSelectComposite: () => void;
};

type PackagingChoice = "component" | "unit";

export const PackagingCreateChoiceModal = ({
    appearance = "legacy",
    open,
    onClose,
    onSelectElement,
    onSelectComposite,
}: PackagingCreateChoiceModalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

    const options: SelectionCardOption<PackagingChoice>[] = [
        {
            id: "component",
            title: t("packaging.create_choice.component.title", "Packaging component"),
            description: t(
                "packaging.create_choice.component.description",
                "A single element that makes up a packaging unit (e.g. the bottle itself, a cap, a label or a film).",
            ),
            icon: <FoodsIcon icon="box" size={4} />,
        },
        {
            id: "unit",
            title: t("packaging.create_choice.unit.title", "Packaging unit"),
            description: t(
                "packaging.create_choice.unit.description",
                "The full packaging of a product, made up of all its components together (e.g. a bottle with its cap and label).",
            ),
            icon: <FoodsIcon icon="boxes" size={4} />,
        },
    ];

    const handleChange = (choice: PackagingChoice) => {
        if (choice === "component") {
            onSelectElement();
        } else {
            onSelectComposite();
        }
    };

    const setOpen: Dispatch<SetStateAction<boolean>> = (nextOpen) => {
        const isOpen = typeof nextOpen === "function" ? nextOpen(open) : nextOpen;

        if (!isOpen) onClose();
    };

    const title = (
        <Typography id="packaging-create-choice-title" component="h2" variant="h5">
            {t("packaging.create_choice.title", "Create a packaging")}
        </Typography>
    );
    const content = (direction: "row" | "column") => (
        <Stack gap={theme.spacing(2)}>
            <Typography variant="body-medium">
                {t("packaging.create_choice.question", "What kind of packaging do you want to create?")}
            </Typography>
            <SelectionCardGroup<PackagingChoice>
                name={t("packaging.create_choice.title", "Create a packaging")}
                options={options}
                onChange={handleChange}
                direction={direction}
                fullWidth
            />
        </Stack>
    );

    if (appearance === "legacy") {
        return (
            <Dialog
                aria-labelledby="packaging-create-choice-title"
                fullWidth
                maxWidth="md"
                onClose={onClose}
                open={open}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.custom.grey[100],
                        borderRadius: theme.spacing(1.5),
                        maxWidth: theme.spacing(92),
                    },
                }}
            >
                <DialogTitle
                    component="div"
                    sx={{
                        alignItems: "center",
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: theme.spacing(8),
                        padding: theme.spacing(2, 2, 2, 3),
                    }}
                >
                    {title}
                    <FoodsIconButton
                        icon="close"
                        size="m"
                        buttonProps={{
                            "aria-label": t("common.actions.close", "Close"),
                            onClick: onClose,
                        }}
                    />
                </DialogTitle>
                <DialogContent sx={{ padding: theme.spacing(3) }}>{content("row")}</DialogContent>
            </Dialog>
        );
    }

    return (
        <FoodsBottomSheetDialog
            open={open}
            setOpen={setOpen}
            PaperProps={{
                sx: {
                    maxWidth: theme.spacing(92),
                    borderRadius: theme.spacing(1.5),
                    backgroundColor: theme.custom.grey[100],
                },
            }}
            title={title}
            content={<Box sx={{ padding: theme.spacing(3) }}>{content(isNarrow ? "column" : "row")}</Box>}
        />
    );
};
